import { useState, useEffect, type ReactNode } from 'react';
import { ordersApi } from '../services/api';
import { downloadInvoicePdf } from '../utils/invoicePdf';

interface LineItem {
  id: string;
  description: string;
  details?: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  category: string;
  isEdited?: boolean;
  originalAmount?: number;
  isCustom?: boolean;
}

interface OrderItemDetail {
  itemNumber: number;
  widthTop: number;
  widthMiddle: number;
  widthBottom: number;
  heightLeft: number;
  heightCenter: number;
  heightRight: number;
  maxWidth: number;
  maxHeight: number;
  systemWidth: number;
  boxWidth: number;
  fabricType: string;
  fabricColor: string;
  frameColor: string;
  motorType: string;
  motorSide: string;
  trackMount: string;
  uChannel: string;
  applyUChannelDeduction: boolean;
  applyBoxDeduction: boolean;
  systemType: string;
  installationType: string;
  brushSize: string;
  biasedCut: boolean;
  biasedCutInches: number;
  notes: string;
  location: string;
}

interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  billTo: {
    companyName: string;
    contactName: string;
    email: string;
    phone: string;
  };
  shipTo: {
    name: string;
    street: string;
    city: string;
    state: string;
    zip: string;
  } | null;
  orderNumber: string;
  poNumber: string;
  sidemark: string;
  sections: {
    screens: LineItem[];
    screenSubtotal: number;
    discountRate: number;
    discountAmount: number;
    afterScreenDiscount: number;
    accessories: LineItem[];
    accessoriesSubtotal: number;
    additionalCharges: LineItem[];
    additionalSubtotal: number;
    openRollDiscount?: number;
    // Promo (personal code or system-wide sale). Emitted by the API when
    // any promo applies to this order — see orders.service.getInvoice.
    promoDiscountAmount?: number;
    promoLabel?: string | null;
  };
  orderItems?: OrderItemDetail[];
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  depositPaid: number;
  achDiscountAmount?: number;
  ccProcessingFee?: number;
  pendingAchAmount?: number;
  balanceDue: number;
  notes: string;
  terms: string;
  dealerNotes?: string;
}

interface DealerInvoiceViewProps {
  order: any;
  onPayInvoice: (paymentMethodType: 'card' | 'us_bank_account' | 'authorize_net' | 'authorize_bank', amount?: number, creditApplied?: number) => void;
  onRefresh: () => void;
  achDiscountPercent?: number;
  authorizeAchDiscountPercent?: number;
  availableCredit?: number;
  /** Optional content rendered to the LEFT of the Totals + payment buttons
   *  block on desktop. Fills the empty band that exists next to the narrow
   *  ml-auto totals column. On mobile stacks above the Totals card. */
  summaryAside?: ReactNode;
}

const fmt = (n: number) => `$${Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const clean = (s: string) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export function DealerInvoiceView({ order, onPayInvoice, onRefresh, achDiscountPercent = 0, authorizeAchDiscountPercent = 0, availableCredit = 0, summaryAside }: DealerInvoiceViewProps) {
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [correctionMode, setCorrectionMode] = useState(false);
  const [correctionMessage, setCorrectionMessage] = useState('');
  const [sendingCorrection, setSendingCorrection] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [paymentOption, setPaymentOption] = useState<'deposit' | 'full'>('deposit');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [creditToApply, setCreditToApply] = useState(0);

  useEffect(() => {
    loadInvoice();
    ordersApi.markInvoiceViewed(order.id).catch(() => {});
  }, [order.id]);


  const loadInvoice = async () => {
    try {
      setLoading(true);
      const data = await ordersApi.getInvoice(order.id);
      setInvoice(data);
    } catch (err) {
      console.error('Failed to load invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCorrection = async () => {
    if (!correctionMessage.trim()) return;
    try {
      setSendingCorrection(true);
      await ordersApi.requestInvoiceCorrection(order.id, correctionMessage.trim());
      setToast({ type: 'success', message: 'Correction request sent to factory' });
      setCorrectionMode(false);
      setCorrectionMessage('');
      onRefresh();
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to send correction request' });
    } finally {
      setSendingCorrection(false);
    }
  };

  const handlePrint = () => {
    if (!invoice) return;
    const w = window.open('', '_blank');
    if (!w) return;

    const screenSpecsHtml = (invoice.orderItems || []).map((it: any) => {
      const screenLine = invoice.sections.screens.find(s => s.description?.includes(`#${it.itemNumber}`) || s.description?.includes(`Unit ${it.itemNumber}`));
      const price = screenLine ? fmt(screenLine.amount) : '';
      return `<div style="margin-bottom:16px;padding:12px;border:1px solid #e5e7eb;border-radius:8px;break-inside:avoid;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <span style="font-weight:700;font-size:15px;">Unit #${it.itemNumber}</span>
          ${price ? `<span style="font-weight:700;font-size:15px;">${price}</span>` : ''}
        </div>
        <div style="font-size:13px;color:#374151;line-height:1.6;">
          <div>Width: T: ${it.widthTop || '-'}" | M: ${it.widthMiddle || '-'}" | B: ${it.widthBottom || '-'}"</div>
          <div>Height: L: ${it.heightLeft || '-'}" | C: ${it.heightCenter || '-'}" | R: ${it.heightRight || '-'}"</div>
          <div>System Width: ${it.systemWidth}" &nbsp;&nbsp; System Height: ${it.maxHeight}" &nbsp;&nbsp; Box Width: ${it.boxWidth}"</div>
          <div>Fabric: ${clean(it.fabricType)} &mdash; ${clean(it.fabricColor)} &nbsp;&nbsp; Frame Color: ${clean(it.frameColor)}</div>
          <div>Installation: ${clean(it.installationType)} &nbsp;&nbsp; Track Mount: ${clean(it.trackMount)}</div>
          <div>Motor: ${it.motorType} &mdash; ${clean(it.motorSide)}</div>
          ${it.uChannel && it.uChannel !== 'none' ? `<div>U-Channel: ${it.uChannel === 'two_sides' ? 'Both Sides' : 'One Side'}</div>` : ''}
          <div>Brush Size: ${it.brushSize || '1 inch'}</div>
          <div>Biased Cut: ${it.biasedCut ? it.biasedCutInches + '"' : 'No'}</div>
          ${it.notes ? `<div style="color:#b45309;margin-top:4px;">Note: ${it.notes}</div>` : ''}
        </div>
      </div>`;
    }).join('');

    const accessoryRows = invoice.sections.accessories.map(a =>
      `<tr><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;" colspan="2">${a.description}${(a as any).details ? `<div style="font-size:11px;color:#6b7280;margin-top:2px;">${(a as any).details}</div>` : ''}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:center;">${a.quantity}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(a.unitPrice)}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(a.amount)}</td></tr>`
    ).join('');

    const additionalRows = invoice.sections.additionalCharges.map(c =>
      `<tr><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;" colspan="2">${c.description}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:center;">${c.quantity}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(c.unitPrice)}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(c.amount)}</td></tr>`
    ).join('');

    const shipToHtml = invoice.shipTo ? `<div><p style="font-weight:600;margin-bottom:4px;">Ship To</p><p>${invoice.shipTo.name || ''}</p><p style="color:#6b7280;">${invoice.shipTo.street || ''}</p><p style="color:#6b7280;">${invoice.shipTo.city || ''}${invoice.shipTo.state ? ', ' + invoice.shipTo.state : ''} ${invoice.shipTo.zip || ''}</p></div>` : '';

    w.document.write(`<!DOCTYPE html><html><head>
      <title>Invoice ${invoice.invoiceNumber}</title>
      <style>* { margin:0;padding:0;box-sizing:border-box; } body { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:800px;margin:0 auto;padding:40px;color:#1f2937;font-size:14px; } table { width:100%;border-collapse:collapse; } @media print { body { padding:20px; } }</style>
    </head><body>
      <div style="display:flex;justify-content:space-between;align-items:start;border-bottom:3px solid #1f2937;padding-bottom:20px;margin-bottom:24px;">
        <div style="display:flex;align-items:start;gap:16px;">
          <img src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png" alt="Keder Screens" style="height:50px;width:auto;" />
          <div>
            <h1 style="font-size:28px;font-weight:800;letter-spacing:-0.5px;">INVOICE</h1>
            <p style="color:#6b7280;margin-top:4px;">${invoice.invoiceNumber}</p>
            ${invoice.orderNumber ? `<p style="color:#6b7280;">Order: ${invoice.orderNumber}</p>` : ''}
            ${invoice.sidemark ? `<p style="color:#6b7280;">Sidemark: ${invoice.sidemark}</p>` : ''}
          </div>
        </div>
        <div style="text-align:right;">
          <p style="font-weight:700;font-size:16px;">Keder Screens</p>
          <p style="color:#6b7280;margin-top:8px;">Date: ${invoice.invoiceDate}</p>
          <p style="color:#6b7280;">Due: ${invoice.dueDate}</p>
        </div>
      </div>
      <div style="display:flex;justify-content:space-between;margin-bottom:28px;">
        <div>
          <p style="font-weight:600;margin-bottom:4px;">Bill To</p>
          <p style="font-weight:500;">${invoice.billTo.companyName}</p>
          <p style="color:#6b7280;">${invoice.billTo.contactName}</p>
          <p style="color:#6b7280;">${invoice.billTo.email}</p>
          ${invoice.billTo.phone ? `<p style="color:#6b7280;">${invoice.billTo.phone}</p>` : ''}
        </div>
        ${shipToHtml}
      </div>
      ${screenSpecsHtml ? `<h3 style="font-size:16px;font-weight:700;margin-bottom:12px;padding-bottom:6px;border-bottom:2px solid #4338ca;color:#4338ca;">Verify Your Screens</h3>${screenSpecsHtml}` : ''}
      <h3 style="font-size:16px;font-weight:700;margin:20px 0 8px;padding-bottom:6px;border-bottom:2px solid #2563eb;">Pricing Breakdown</h3>
      <table>
        <thead><tr style="background:#f9fafb;"><th style="padding:8px 10px;text-align:left;font-weight:600;">Description</th><th style="padding:8px 10px;text-align:left;font-weight:600;">Details</th><th style="padding:8px 10px;text-align:center;font-weight:600;">Qty</th><th style="padding:8px 10px;text-align:right;font-weight:600;">Unit Price</th><th style="padding:8px 10px;text-align:right;font-weight:600;">Amount</th></tr></thead>
        <tbody>
          ${invoice.sections.screens.map(s => `<tr><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;">${s.description}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;color:#6b7280;font-size:13px;">${s.details || ''}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:center;">${s.quantity}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(s.unitPrice)}</td><td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(s.amount)}</td></tr>`).join('')}
          <tr style="background:#f9fafb;"><td style="padding:8px 10px;font-weight:600;" colspan="4">Screen Subtotal</td><td style="padding:8px 10px;text-align:right;font-weight:600;">${fmt(invoice.sections.screenSubtotal)}</td></tr>
          <tr><td style="padding:8px 10px;color:#059669;" colspan="4">Dealer Discount (${invoice.sections.discountRate}%)</td><td style="padding:8px 10px;text-align:right;color:#059669;">-${fmt(invoice.sections.discountAmount)}</td></tr>
          <tr style="background:#f0fdf4;border-bottom:2px solid #bbf7d0;"><td style="padding:10px;font-weight:700;" colspan="4">Screens After Discount</td><td style="padding:10px;text-align:right;font-weight:700;">${fmt(invoice.sections.afterScreenDiscount)}</td></tr>
          ${(invoice.sections.openRollDiscount || 0) > 0 ? `<tr style="background:#f0fdf4;"><td style="padding:8px 10px;color:#15803d;font-weight:500;" colspan="4">Open Roll Discount (4% off dealer cost)</td><td style="padding:8px 10px;text-align:right;color:#15803d;font-weight:500;">-${fmt(invoice.sections.openRollDiscount || 0)}</td></tr>` : ''}
          ${(invoice.sections.promoDiscountAmount || 0) > 0 ? `<tr style="background:#f0fdf4;"><td style="padding:8px 10px;color:#15803d;font-weight:500;" colspan="4">Promo${invoice.sections.promoLabel ? ` (${invoice.sections.promoLabel})` : ''}</td><td style="padding:8px 10px;text-align:right;color:#15803d;font-weight:500;">-${fmt(invoice.sections.promoDiscountAmount || 0)}</td></tr>` : ''}
        </tbody>
      </table>
      ${invoice.sections.accessories.length > 0 ? `<table style="margin-top:16px;"><thead><tr style="background:#fefce8;"><th style="padding:8px;text-align:left;color:#92400e;font-weight:600;" colspan="5">Accessories & Add-ons</th></tr></thead><tbody>${accessoryRows}<tr style="background:#f9fafb;"><td style="padding:8px 10px;font-weight:600;" colspan="4">Accessories Subtotal</td><td style="padding:8px 10px;text-align:right;font-weight:600;">${fmt(invoice.sections.accessoriesSubtotal)}</td></tr></tbody></table>` : ''}
      ${invoice.sections.additionalCharges.length > 0 ? `<table style="margin-top:16px;"><thead><tr style="background:#f3f4f6;"><th style="padding:8px;text-align:left;font-weight:600;" colspan="5">Additional Charges</th></tr></thead><tbody>${additionalRows}<tr style="background:#f9fafb;"><td style="padding:8px 10px;font-weight:600;" colspan="4">Additional Charges Subtotal</td><td style="padding:8px 10px;text-align:right;font-weight:600;">${fmt(invoice.sections.additionalSubtotal)}</td></tr></tbody></table>` : ''}
      <table style="margin-top:24px;width:50%;margin-left:auto;">
        <tbody>
          <tr><td style="padding:6px 10px;font-weight:500;">Subtotal</td><td style="padding:6px 10px;text-align:right;">${fmt(invoice.subtotal)}</td></tr>
          ${invoice.taxAmount > 0 ? `<tr><td style="padding:6px 10px;">Tax${invoice.subtotal > 0 ? ` <span style="color:#9ca3af;font-size:11px;">(${((invoice.taxAmount / invoice.subtotal) * 100).toFixed(2)}%)</span>` : ''}</td><td style="padding:6px 10px;text-align:right;">${fmt(invoice.taxAmount)}</td></tr>` : ''}
          <tr style="border-top:2px solid #1f2937;"><td style="padding:10px;font-weight:800;font-size:16px;">Total</td><td style="padding:10px;text-align:right;font-weight:800;font-size:16px;">${fmt(invoice.totalAmount)}</td></tr>
          ${invoice.depositPaid > 0 ? `<tr><td style="padding:6px 10px;color:#6b7280;">Less: Payments Received</td><td style="padding:6px 10px;text-align:right;color:#6b7280;">-${fmt(invoice.depositPaid)}</td></tr>` : ''}
          ${(invoice.ccProcessingFee || 0) > 0 ? `<tr><td style="padding:6px 10px;color:#6b7280;">Credit Card Processing Fee <span style="color:#9ca3af;font-size:11px;">(charged to card)</span></td><td style="padding:6px 10px;text-align:right;color:#6b7280;">${fmt(invoice.ccProcessingFee || 0)}</td></tr>` : ''}
          ${(invoice.achDiscountAmount || 0) > 0 ? `<tr><td style="padding:6px 10px;color:#059669;">Less: ACH Payment Discount</td><td style="padding:6px 10px;text-align:right;color:#059669;">-${fmt(invoice.achDiscountAmount || 0)}</td></tr>` : ''}
          <tr style="background:#fef3c7;border:2px solid #f59e0b;"><td style="padding:12px;font-weight:800;font-size:18px;">Balance Due</td><td style="padding:12px;text-align:right;font-weight:800;font-size:18px;">${fmt(invoice.balanceDue)}</td></tr>
        </tbody>
      </table>
      ${invoice.notes ? `<div style="margin-top:28px;padding:16px;background:#f9fafb;border-radius:6px;"><p style="font-weight:600;margin-bottom:4px;">Comments / Special Instructions</p><p style="color:#6b7280;white-space:pre-wrap;">${invoice.notes}</p></div>` : ''}
      <div style="margin-top:20px;padding:16px;background:#f9fafb;border-radius:6px;"><p style="font-weight:600;margin-bottom:4px;">Terms</p><p style="color:#6b7280;">${invoice.terms || '50% deposit to start production. Remaining balance due before shipping/pickup.'}</p></div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const handleDownloadPdf = async () => {
    if (!invoice || downloadingPdf) return;
    try {
      setDownloadingPdf(true);
      await downloadInvoicePdf(order.id, invoice.invoiceNumber);
    } catch (err) {
      console.error('PDF download failed:', err);
      setToast({ type: 'error', message: 'PDF download failed. Try Print instead.' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg shadow p-8 text-center" style={{ background: 'var(--bg-card)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-3" style={{ borderColor: '#FFCD24' }} />
        <p className="text-gray-500">Loading invoice...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="rounded-lg shadow p-8 text-center" style={{ background: 'var(--bg-card)' }}>
        <p className="text-gray-500">Invoice not yet available for this order.</p>
      </div>
    );
  }

  const hasBalance = invoice.balanceDue > 0.5;
  const paymentProcessing = (order.paymentStatus || (order as any).payment_status) === 'processing';
  const correctionPending = !!(order.invoiceCorrectionRequestedAt || (order as any).invoice_correction_requested_at);
  const correctionResolved = !correctionPending && !!(order.invoiceCorrectionResolvedAt || (order as any).invoice_correction_resolved_at);
  const productionLocked = ['in_production', 'ready_to_ship', 'shipped', 'delivered', 'closed', 'completed', 'installed', 'warranty_registered'].includes(order.status);

  // Find price for each screen item
  const getScreenPrice = (itemNum: number): string => {
    const line = invoice.sections.screens.find(s =>
      s.description?.includes(`#${itemNum}`) || s.description?.includes(`Unit ${itemNum}`)
    );
    return line ? fmt(line.amount) : '';
  };

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.message}
          <button onClick={() => setToast(null)} className="ml-3 font-bold">&times;</button>
        </div>
      )}

      {/* ACH Payment Processing Banner */}
      {paymentProcessing && (
        <div className="p-4 rounded-lg flex items-start gap-3" style={{ background: 'rgba(99, 91, 255, 0.12)', border: '1px solid rgba(99, 91, 255, 0.3)' }}>
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#818cf8' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-semibold text-sm" style={{ color: '#818cf8' }}>Bank Payment Processing</p>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(129, 140, 248, 0.8)' }}>
              Your ACH bank transfer has been submitted and is being processed. Bank transfers typically take 3-5 business days to clear. Your order will be confirmed once payment is received.
            </p>
          </div>
        </div>
      )}

      {/* Payment Summary */}
      {(invoice.depositPaid > 0 || (invoice.achDiscountAmount || 0) > 0 || (invoice.ccProcessingFee || 0) > 0 || (invoice.pendingAchAmount || 0) > 0) && (
        <div className="p-3 rounded-lg" style={{ background: 'rgba(34, 197, 94, 0.12)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
          {invoice.depositPaid > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium" style={{ color: '#4ade80' }}>Payments Received</p>
              <p className="font-semibold" style={{ color: '#4ade80' }}>{fmt(invoice.depositPaid)}</p>
            </div>
          )}
          {(invoice.pendingAchAmount || 0) > 0 && (
            <div className="flex items-center justify-between mt-1">
              <p className="text-sm font-medium" style={{ color: '#818cf8' }}>Payment Pending (ACH clearing)</p>
              <p className="font-semibold" style={{ color: '#818cf8' }}>{fmt(invoice.pendingAchAmount || 0)}</p>
            </div>
          )}
          {(invoice.ccProcessingFee || 0) > 0 && (
            <div className="flex items-center justify-between mt-1">
              <p className="text-sm font-medium" style={{ color: '#34d399' }}>Credit Card Processing Fee</p>
              <p className="font-semibold" style={{ color: '#34d399' }}>{fmt(invoice.ccProcessingFee || 0)}</p>
            </div>
          )}
          {(invoice.achDiscountAmount || 0) > 0 && (
            <div className="flex items-center justify-between mt-1">
              <p className="text-sm font-medium" style={{ color: '#34d399' }}>ACH Payment Discount</p>
              <p className="font-semibold" style={{ color: '#34d399' }}>{fmt(invoice.achDiscountAmount || 0)}</p>
            </div>
          )}
          {(invoice.pendingAchAmount || 0) > 0 ? (
            <p className="text-xs mt-1" style={{ color: 'rgba(129, 140, 248, 0.9)' }}>
              Remaining after pending payment clears: <span className="font-semibold">{fmt(Math.max(0, invoice.balanceDue - (invoice.pendingAchAmount || 0)))}</span>
              {' — no payment needed while ACH is processing'}
            </p>
          ) : (
            <p className="text-xs mt-1" style={{ color: 'rgba(74, 222, 128, 0.7)' }}>
              Balance Due: <span className="font-semibold">{fmt(invoice.balanceDue)}</span>
              {invoice.balanceDue <= 0.5 && ' — Fully paid'}
            </p>
          )}
        </div>
      )}

      {/* Correction pending banner */}
      {correctionPending && (
        <div className="p-4 rounded-lg" style={{ background: 'rgba(168, 85, 247, 0.12)', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
          <p className="text-sm font-semibold flex items-center gap-2" style={{ color: '#c084fc' }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
            Correction Requested
          </p>
          <p className="text-sm mt-1" style={{ color: 'rgba(192, 132, 252, 0.7)' }}>
            Your correction request has been sent to the factory. They will review and update the invoice.
          </p>
        </div>
      )}

      {/* Correction resolved banner */}
      {correctionResolved && (
        <div className="p-4 rounded-lg" style={{ background: 'rgba(34, 197, 94, 0.12)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
          <p className="text-sm font-semibold flex items-center gap-2" style={{ color: '#4ade80' }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            Correction Made
          </p>
          <p className="text-sm mt-1" style={{ color: 'rgba(74, 222, 128, 0.7)' }}>
            The factory has reviewed your correction request and updated the invoice. Please review the changes below.
          </p>
        </div>
      )}

      {/* Shipping adjustment banner */}
      {(order.shippingAdjustmentStatus === 'pending' || (order as any).shipping_adjustment_status === 'pending') && (
        <div className="p-3 rounded-lg" style={{ background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
          <p className="font-medium text-sm" style={{ color: '#fbbf24' }}>
            Shipping Adjustment: {fmt(order.shippingAdjustmentAmount || (order as any).shipping_adjustment_amount || 0)} due
          </p>
          <p className="text-sm" style={{ color: 'rgba(251, 191, 36, 0.7)' }}>
            Due by {new Date(order.shippingAdjustmentDueDate || (order as any).shipping_adjustment_due_date).toLocaleDateString()} (Net 15)
          </p>
        </div>
      )}

      {/* Invoice adjusted badge */}
      {(order.invoiceAdjusted || (order as any).invoice_adjusted) && (
        <div className="p-3 rounded-lg" style={{ background: 'rgba(59, 130, 246, 0.12)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
          <p className="text-sm" style={{ color: '#60a5fa' }}>
            <span className="font-medium">Invoice Adjusted</span> — The factory has updated this invoice. Please review the changes before paying.
          </p>
        </div>
      )}

      {/* Action Bar */}
      <div className="rounded-lg shadow px-4 py-3 flex items-center justify-between sticky top-0 z-10" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <div className="flex items-center gap-3">
          <h2 className="font-semibold text-lg text-gray-900">Invoice</h2>
          {!hasBalance && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#4ade80' }}>
              Paid in Full
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="px-4 py-1.5 text-sm rounded transition-colors font-medium"
            style={{ background: 'var(--bg-hover)', color: 'var(--text-primary)' }}
          >
            Print
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            className="px-4 py-1.5 text-sm rounded transition-colors font-medium flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)' }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            {downloadingPdf ? 'Generating...' : 'Download PDF'}
          </button>
          {!correctionPending && !productionLocked && (
            <button
              onClick={() => setCorrectionMode(!correctionMode)}
              className="px-4 py-1.5 text-sm rounded transition"
              style={{ background: 'rgba(168, 85, 247, 0.12)', color: '#c084fc', border: '1px solid rgba(168, 85, 247, 0.3)' }}
            >
              Request Correction
            </button>
          )}
        </div>
      </div>

      {/* Correction request form */}
      {correctionMode && (
        <div className="p-4 rounded-lg" style={{ background: 'rgba(168, 85, 247, 0.08)', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
          <label className="block text-sm font-medium mb-2" style={{ color: '#c084fc' }}>
            What needs to be corrected?
          </label>
          <textarea
            value={correctionMessage}
            onChange={(e) => setCorrectionMessage(e.target.value)}
            placeholder="e.g., Was promised 60% discount by Mike, shipping should be $450..."
            className="w-full p-3 rounded-lg text-sm focus:outline-none"
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)' }}
            rows={3}
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleRequestCorrection}
              disabled={sendingCorrection || !correctionMessage.trim()}
              className="px-4 py-2 text-sm text-white rounded-lg transition disabled:opacity-50"
              style={{ background: '#9333ea' }}
            >
              {sendingCorrection ? 'Sending...' : 'Send to Factory'}
            </button>
            <button
              onClick={() => { setCorrectionMode(false); setCorrectionMessage(''); }}
              className="px-4 py-2 text-sm rounded-lg transition text-gray-500"
              style={{ background: 'var(--bg-hover)' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Invoice Header */}
      <div className="rounded-lg shadow p-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <div className="flex justify-between items-start pb-4 mb-4" style={{ borderBottom: '2px solid var(--border-color)' }}>
          <div className="flex items-start gap-4">
            <img src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png" alt="Keder Screens" className="h-12 w-auto" />
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">INVOICE</h1>
              <p className="text-gray-500 mt-1">{invoice.invoiceNumber}</p>
              <p className="text-gray-500 text-sm">Order: {invoice.orderNumber}</p>
              {invoice.sidemark && <p className="text-gray-500 text-sm">Sidemark: {invoice.sidemark}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="font-bold text-lg text-gray-900">Keder Screens</p>
            <div className="mt-2 text-sm space-y-0.5">
              <p className="text-gray-500">Date: <span className="font-medium text-gray-700">{invoice.invoiceDate}</span></p>
              <p className="text-gray-500">Due: <span className="font-medium text-gray-700">{invoice.dueDate}</span></p>
            </div>
          </div>
        </div>

        {/* Bill To / Ship To */}
        <div className="grid grid-cols-2 gap-8">
          <div>
            <p className="font-semibold text-gray-700 mb-1">Bill To</p>
            <p className="font-medium text-gray-900">{invoice.billTo.companyName}</p>
            <p className="text-gray-500 text-sm">{invoice.billTo.contactName}</p>
            <p className="text-gray-500 text-sm">{invoice.billTo.email}</p>
            {invoice.billTo.phone && <p className="text-gray-500 text-sm">{invoice.billTo.phone}</p>}
          </div>
          {invoice.shipTo && (
            <div>
              <p className="font-semibold text-gray-700 mb-1">Ship To</p>
              {invoice.shipTo.name && <p className="text-sm text-gray-900">{invoice.shipTo.name}</p>}
              {invoice.shipTo.street && <p className="text-gray-500 text-sm">{invoice.shipTo.street}</p>}
              <p className="text-gray-500 text-sm">
                {invoice.shipTo.city}{invoice.shipTo.state ? ', ' + invoice.shipTo.state : ''} {invoice.shipTo.zip}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Order Details — Verify Your Screens (Print-style format) */}
      {invoice.orderItems && invoice.orderItems.length > 0 && (
        <div className="rounded-lg shadow overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
          <div className="px-4 py-2.5" style={{ background: 'rgba(99, 102, 241, 0.12)', borderBottom: '2px solid rgba(99, 102, 241, 0.35)' }}>
            <h3 className="font-semibold" style={{ color: '#818cf8' }}>Verify Your Screens</h3>
          </div>
          <div>
            {invoice.orderItems.map((item, idx) => {
              const price = getScreenPrice(item.itemNumber);
              return (
                <div
                  key={item.itemNumber}
                  className="px-5 py-4"
                  style={{ borderBottom: idx < (invoice.orderItems?.length || 0) - 1 ? '1px solid var(--border-color)' : 'none' }}
                >
                  {/* Unit header with price */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-bold text-gray-900" style={{ fontSize: '15px' }}>
                      Unit #{item.itemNumber}
                    </span>
                    {price && (
                      <span className="font-bold text-gray-900" style={{ fontSize: '15px' }}>{price}</span>
                    )}
                  </div>

                  {/* Specs in print format */}
                  <div className="space-y-1 text-sm" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', color: 'var(--text-secondary)' }}>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Width:</span>{' '}
                      <span className="text-gray-900">T: {item.widthTop || '-'}" | M: {item.widthMiddle || '-'}" | B: {item.widthBottom || '-'}"</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Height:</span>{' '}
                      <span className="text-gray-900">L: {item.heightLeft || '-'}" | C: {item.heightCenter || '-'}" | R: {item.heightRight || '-'}"</span>
                    </div>
                    <div className="flex gap-6">
                      <span>
                        <span style={{ color: 'var(--text-muted)' }}>System Width:</span>{' '}
                        <span className="font-semibold" style={{ color: '#60a5fa' }}>{item.systemWidth}"</span>
                      </span>
                      <span>
                        <span style={{ color: 'var(--text-muted)' }}>System Height:</span>{' '}
                        <span className="font-semibold" style={{ color: '#60a5fa' }}>{item.maxHeight}"</span>
                      </span>
                      <span>
                        <span style={{ color: 'var(--text-muted)' }}>Box Width:</span>{' '}
                        <span className="font-semibold" style={{ color: '#818cf8' }}>{item.boxWidth}"</span>
                      </span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Fabric:</span>{' '}
                      <span className="text-gray-900">{clean(item.fabricType)} — {clean(item.fabricColor)}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Frame Color:</span>{' '}
                      <span className="text-gray-900">{clean(item.frameColor)}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Installation:</span>{' '}
                      <span className="text-gray-900">{clean(item.installationType)}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Track Mount:</span>{' '}
                      <span className="text-gray-900">{clean(item.trackMount)}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Motor:</span>{' '}
                      <span className="text-gray-900">{clean(item.motorType)} — {clean(item.motorSide)}</span>
                    </div>
                    {item.uChannel && item.uChannel !== 'none' && (
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>U-Channel:</span>{' '}
                        <span className="text-gray-900">{item.uChannel === 'two_sides' ? 'Both Sides' : 'One Side'}</span>
                      </div>
                    )}
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Brush Size:</span>{' '}
                      <span className="text-gray-900">{item.brushSize || '1 inch'}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Biased Cut:</span>{' '}
                      <span className="text-gray-900">{item.biasedCut ? item.biasedCutInches + '"' : 'No'}</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Factory Deductions:</span>{' '}
                      <span className="text-gray-900">
                        {(() => {
                          // applyBoxDeduction defaults TRUE (deduct ¼" from
                          // box width). Only when explicitly false has the
                          // dealer opted out of it. Prior wording inverted
                          // both signals — said "No Deductions Applied" on
                          // the default-on case (which is when the ¼" box
                          // deduction IS applied — proven by Box Width
                          // 119.75" on a 120" system).
                          const parts: string[] = [];
                          if (item.applyBoxDeduction !== false) parts.push('¼" Box Deduction Applied');
                          if (item.applyUChannelDeduction) parts.push('U-Channel Deduction Applied');
                          return parts.length ? parts.join(', ') : 'No Deductions Applied';
                        })()}
                      </span>
                    </div>
                    {item.notes && (
                      <div className="mt-1 px-2 py-1 rounded" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#fbbf24' }}>
                        Note: {item.notes}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Screens Pricing */}
      <div className="rounded-lg shadow overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <div className="px-4 py-2.5" style={{ background: 'rgba(59, 130, 246, 0.12)', borderBottom: '2px solid rgba(59, 130, 246, 0.35)' }}>
          <h3 className="font-semibold" style={{ color: '#60a5fa' }}>Screens</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-sm" style={{ background: 'var(--bg-table-header)' }}>
              <th className="text-left px-4 py-2 font-semibold text-gray-600">Description</th>
              <th className="text-left px-4 py-2 font-semibold text-gray-600">Details</th>
              <th className="text-center px-4 py-2 font-semibold text-gray-600 w-16">Qty</th>
              <th className="text-right px-4 py-2 font-semibold text-gray-600 w-28">Unit Price</th>
              <th className="text-right px-4 py-2 font-semibold text-gray-600 w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.sections.screens.map((s, i) => (
              <tr key={s.id || i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td className="px-4 py-2.5 text-sm text-gray-900">{s.description}</td>
                <td className="px-4 py-2.5 text-sm text-gray-500">{s.details || ''}</td>
                <td className="px-4 py-2.5 text-sm text-center text-gray-900">{s.quantity}</td>
                <td className="px-4 py-2.5 text-sm text-right text-gray-900">{fmt(s.unitPrice)}</td>
                <td className="px-4 py-2.5 text-sm text-right font-medium text-gray-900">{fmt(s.amount)}</td>
              </tr>
            ))}
            <tr style={{ background: 'var(--bg-table-header)', borderBottom: '1px solid var(--border-color)' }}>
              <td className="px-4 py-2 font-semibold text-sm text-gray-700" colSpan={4}>Screen Subtotal</td>
              <td className="px-4 py-2 text-right font-semibold text-sm text-gray-700">{fmt(invoice.sections.screenSubtotal)}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
              <td className="px-4 py-2 text-sm" style={{ color: '#4ade80' }} colSpan={4}>Dealer Discount ({invoice.sections.discountRate}%)</td>
              <td className="px-4 py-2 text-right text-sm font-medium" style={{ color: '#4ade80' }}>-{fmt(invoice.sections.discountAmount)}</td>
            </tr>
            <tr style={{ background: 'rgba(34, 197, 94, 0.1)', borderBottom: '2px solid rgba(34, 197, 94, 0.3)' }}>
              <td className="px-4 py-2.5 font-bold text-sm text-gray-900" colSpan={4}>Screens After Discount</td>
              <td className="px-4 py-2.5 text-right font-bold text-sm text-gray-900">{fmt(invoice.sections.afterScreenDiscount)}</td>
            </tr>
            {(invoice.sections.openRollDiscount || 0) > 0 && (
              <tr style={{ background: 'rgba(34, 197, 94, 0.06)' }}>
                <td className="px-4 py-2 text-sm font-medium" style={{ color: '#15803d' }} colSpan={4}>
                  Open Roll Discount (4% off dealer cost)
                </td>
                <td className="px-4 py-2 text-right text-sm font-medium" style={{ color: '#15803d' }}>
                  -{fmt(invoice.sections.openRollDiscount || 0)}
                </td>
              </tr>
            )}
            {(invoice.sections.promoDiscountAmount || 0) > 0 && (
              <tr style={{ background: 'rgba(34, 197, 94, 0.06)' }}>
                <td className="px-4 py-2 text-sm font-medium" style={{ color: '#15803d' }} colSpan={4}>
                  Promo{invoice.sections.promoLabel ? ` (${invoice.sections.promoLabel})` : ''}
                </td>
                <td className="px-4 py-2 text-right text-sm font-medium" style={{ color: '#15803d' }}>
                  -{fmt(invoice.sections.promoDiscountAmount || 0)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Accessories Section */}
      {invoice.sections.accessories.length > 0 && (
        <div className="rounded-lg shadow overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
          <div className="px-4 py-2.5" style={{ background: 'rgba(245, 158, 11, 0.12)', borderBottom: '2px solid rgba(245, 158, 11, 0.35)' }}>
            <h3 className="font-semibold" style={{ color: '#fbbf24' }}>Accessories & Add-ons</h3>
          </div>
          <table className="w-full">
            <thead>
              <tr className="text-sm" style={{ background: 'var(--bg-table-header)' }}>
                <th className="text-left px-4 py-2 font-semibold text-gray-600" colSpan={2}>Description</th>
                <th className="text-center px-4 py-2 font-semibold text-gray-600 w-16">Qty</th>
                <th className="text-right px-4 py-2 font-semibold text-gray-600 w-28">Unit Price</th>
                <th className="text-right px-4 py-2 font-semibold text-gray-600 w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.sections.accessories.map((a, i) => (
                <tr key={a.id || i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td className="px-4 py-2.5 text-sm text-gray-900" colSpan={2}>
                    {a.description}
                    {(a as any).details && (
                      <div className="text-xs text-gray-500 mt-0.5">{(a as any).details}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-center text-gray-900">{a.quantity}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-gray-900">{fmt(a.unitPrice)}</td>
                  <td className="px-4 py-2.5 text-sm text-right font-medium text-gray-900">{fmt(a.amount)}</td>
                </tr>
              ))}
              <tr style={{ background: 'var(--bg-table-header)' }}>
                <td className="px-4 py-2 font-semibold text-sm text-gray-700" colSpan={4}>Accessories Subtotal</td>
                <td className="px-4 py-2 text-right font-semibold text-sm text-gray-700">{fmt(invoice.sections.accessoriesSubtotal)}</td>
              </tr>
            </tbody>
          </table>
          <p className="px-4 py-2 text-xs italic" style={{ color: 'var(--text-muted)' }}>Accessories are not subject to dealer discount</p>
        </div>
      )}

      {/* Additional Charges Section */}
      {invoice.sections.additionalCharges.length > 0 && (
        <div className="rounded-lg shadow overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
          <div className="px-4 py-2.5" style={{ background: 'var(--bg-hover)', borderBottom: '2px solid var(--border-color)' }}>
            <h3 className="font-semibold text-gray-700">Additional Charges</h3>
          </div>
          <table className="w-full">
            <tbody>
              {invoice.sections.additionalCharges.map((c, i) => (
                <tr key={c.id || i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td className="px-4 py-2.5 text-sm text-gray-900" colSpan={2}>
                    {c.description}
                    {c.isEdited && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa' }}>Adjusted</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-center text-gray-900 w-16">{c.quantity}</td>
                  <td className="px-4 py-2.5 text-sm text-right text-gray-900 w-28">{fmt(c.unitPrice)}</td>
                  <td className="px-4 py-2.5 text-sm text-right font-medium text-gray-900 w-28">{fmt(c.amount)}</td>
                </tr>
              ))}
              <tr style={{ background: 'var(--bg-table-header)' }}>
                <td className="px-4 py-2 font-semibold text-sm text-gray-700" colSpan={4}>Additional Charges Subtotal</td>
                <td className="px-4 py-2 text-right font-semibold text-sm text-gray-700">{fmt(invoice.sections.additionalSubtotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Totals — wrap in a 2-col grid when an aside is provided so the
          summary fills what used to be empty space to the left of the
          narrow totals column. Without aside: behaves exactly as before. */}
      <div className="rounded-lg shadow p-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <div className={summaryAside ? 'lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start' : ''}>
          {summaryAside && (
            <div className="mb-6 lg:mb-0">{summaryAside}</div>
          )}
        <div className="ml-auto w-72 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium text-gray-700">Subtotal</span>
            <span className="text-gray-900">{fmt(invoice.subtotal)}</span>
          </div>
          {invoice.taxAmount > 0 && (
            <div className="flex justify-between text-sm text-gray-500">
              <span>Tax {invoice.subtotal > 0 && (
                <span className="text-xs text-gray-400">({((invoice.taxAmount / invoice.subtotal) * 100).toFixed(2)}%)</span>
              )}</span>
              <span>{fmt(invoice.taxAmount)}</span>
            </div>
          )}
          <div className="flex justify-between pt-2" style={{ borderTop: '2px solid var(--border-color)' }}>
            <span className="font-bold text-lg text-gray-900">Total</span>
            <span className="font-bold text-lg text-gray-900">{fmt(invoice.totalAmount)}</span>
          </div>
          {invoice.depositPaid > 0 && (
            <div className="flex justify-between text-sm text-gray-500">
              <span>Less: Payments Received</span>
              <span>-{fmt(invoice.depositPaid)}</span>
            </div>
          )}
          {(invoice.achDiscountAmount || 0) > 0 && (
            <div className="flex justify-between text-sm" style={{ color: '#059669' }}>
              <span>Less: ACH Payment Discount</span>
              <span>-{fmt(invoice.achDiscountAmount || 0)}</span>
            </div>
          )}
          <div className="flex justify-between p-3 rounded-lg" style={{ background: 'rgba(255, 205, 36, 0.15)', border: '2px solid #FFCD24' }}>
            <span className="font-extrabold text-lg" style={{ color: '#FFCD24' }}>Balance Due</span>
            <span className="font-extrabold text-lg" style={{ color: '#FFCD24' }}>{fmt(invoice.balanceDue)}</span>
          </div>
          {hasBalance && !paymentProcessing && (() => {
            const hasDeposit = invoice.depositPaid > 0.5;
            const depositAmount = Math.ceil(invoice.balanceDue * 50) / 100;
            const selectedAmount = hasDeposit ? invoice.balanceDue : (paymentOption === 'deposit' ? depositAmount : invoice.balanceDue);
            const achAmount = selectedAmount * (1 - achDiscountPercent / 100);
            const authorizeAchAmount = selectedAmount * (1 - authorizeAchDiscountPercent / 100);
            return (
              <div className="mt-4 space-y-3">
                {!hasDeposit && (
                  <div className="flex rounded-lg overflow-hidden text-sm font-medium w-full" style={{ border: '1px solid var(--border-color)' }}>
                    <button
                      onClick={() => setPaymentOption('deposit')}
                      className="flex-1 px-4 py-2 transition-colors text-center"
                      style={paymentOption === 'deposit'
                        ? { background: 'rgba(255, 205, 36, 0.2)', color: '#FFCD24', borderRight: '1px solid var(--border-color)' }
                        : { background: 'var(--bg-hover)', color: 'var(--text-secondary)', borderRight: '1px solid var(--border-color)' }}
                    >
                      50% Deposit ({fmt(depositAmount)})
                    </button>
                    <button
                      onClick={() => setPaymentOption('full')}
                      className="flex-1 px-4 py-2 transition-colors text-center"
                      style={paymentOption === 'full'
                        ? { background: 'rgba(255, 205, 36, 0.2)', color: '#FFCD24' }
                        : { background: 'var(--bg-hover)', color: 'var(--text-secondary)' }}
                    >
                      Pay in Full ({fmt(invoice.balanceDue)})
                    </button>
                  </div>
                )}
                {/* Apply store credit (if any on file) */}
                {availableCredit > 0 && (
                  <div className="rounded-lg p-3 mb-2" style={{ background: 'var(--bg-hover)', border: '1px solid var(--border-color)' }}>
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span style={{ color: 'var(--text-secondary)' }}>Store credit on file</span>
                      <span className="font-mono text-green-500">{fmt(availableCredit)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>Apply $</label>
                      <input
                        type="number"
                        min={0}
                        max={Math.min(availableCredit, Math.max(0, selectedAmount - 1))}
                        step={0.01}
                        value={creditToApply}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          const safe = isNaN(v) ? 0 : Math.max(0, Math.min(v, availableCredit, Math.max(0, selectedAmount - 1)));
                          setCreditToApply(Math.round(safe * 100) / 100);
                        }}
                        className="flex-1 text-right rounded px-2 py-1 font-mono text-sm"
                        style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                      />
                      <button
                        type="button"
                        onClick={() => setCreditToApply(Math.min(availableCredit, Math.max(0, selectedAmount - 1)))}
                        className="text-xs px-2 py-1 rounded"
                        style={{ background: 'var(--bg-card)', color: 'var(--text-secondary)' }}
                      >Max</button>
                    </div>
                    {creditToApply > 0 && (
                      <p className="text-[11px] mt-1" style={{ color: 'var(--text-secondary)' }}>
                        Card/bank charge: <span className="font-mono">{fmt(Math.max(0, selectedAmount - creditToApply))}</span>
                      </p>
                    )}
                  </div>
                )}

                {/* Payment processor buttons — Stripe for CC + ACH */}
                <div className="flex gap-2">
                  <button
                    onClick={() => onPayInvoice('card', selectedAmount, creditToApply > 0 ? creditToApply : undefined)}
                    className="flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors"
                    style={{ background: '#635bff', color: '#fff' }}
                  >
                    Pay with Card ({fmt(Math.max(0, selectedAmount - creditToApply))})
                  </button>
                  <button
                    onClick={() => onPayInvoice('us_bank_account', selectedAmount, creditToApply > 0 ? creditToApply : undefined)}
                    className="flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors"
                    style={{ background: '#16a34a', color: '#fff' }}
                    title={achDiscountPercent > 0 ? `Save ${achDiscountPercent}% by paying with bank account` : undefined}
                  >
                    Pay with Bank ({fmt(Math.max(0, achAmount - creditToApply))})
                    {achDiscountPercent > 0 && <span className="ml-1 opacity-75">-{achDiscountPercent}%</span>}
                  </button>
                </div>
                {/* Authorize.net / Helcim removed — all payments via Stripe */}
              </div>
            );
          })()}
        </div>
        </div>{/* /grid wrapper for summaryAside */}
      </div>

      {/* Notes & Terms */}
      <div className="rounded-lg shadow p-6 grid grid-cols-1 md:grid-cols-2 gap-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        {invoice.notes && (
          <div>
            <p className="font-semibold text-sm text-gray-700 mb-1">Comments / Special Instructions</p>
            <p className="text-gray-500 text-sm whitespace-pre-wrap">{invoice.notes}</p>
          </div>
        )}
        <div>
          <p className="font-semibold text-sm text-gray-700 mb-1">Payment Terms</p>
          <p className="text-gray-500 text-sm">{invoice.terms || '50% deposit to start production. Remaining balance due before shipping/pickup.'}</p>
        </div>
      </div>
    </div>
  );
}
